<template>
    <v-dialog v-model="dialog" max-width="700" persistent>
        <template v-slot:activator="{ on: dialogEvents, attrs }">
            <div v-if="$scopedSlots.activator" v-bind="attrs" v-on="dialogEvents">
                <slot name="activator"></slot>
            </div>
            <v-btn v-else color="error" text v-bind="attrs" v-on="dialogEvents">{{ defaultButtonText }}</v-btn>
        </template>

        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                <div class="d-flex align-center secondary--text">
                    <span>{{ dialogTitle }}</span>
                </div>

                <v-spacer></v-spacer>
                <v-btn :disabled="leaving" @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <v-alert prominent type="error" text>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold mb-2">Access loss</span>
                        {{ warningText }}
                        <v-divider class="mt-4 error"></v-divider>
                        <v-checkbox light color="error" v-model="agreeBox">
                            <template v-slot:label>
                                <span>I understand</span>
                            </template>
                        </v-checkbox>
                    </div>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <div>
                    <v-btn :loading="leaving" @click="performLeave" :disabled="!agreeBox || !effectiveEntityId" color="error" text>leave</v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as instanceErrors from '@/modules/instance/errors'
import * as invitationsErrors from '@/mixins/errors/invitations'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as organizationErrors from '@/modules/home/errors/organizations'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(
    instanceErrors,
    invitationsErrors,
    lifecycleErrors,
    organizationErrors,
    permissionsErrors,
    spaceErrors
)

export default {
    name: 'EntityLeaveDialog',
    mixins: [enumsData],
    props: {
        entityType: {
            type: String,
            required: true,
            validator: value => ['organization', 'space'].includes(value)
        }
    },
    data() {
        return {
            dialog: false,
            agreeBox: false,
            leaving: false
        }
    },
    computed: {
        effectiveEntityId() {
            if (this.entityType === 'organization') {
                return this.$route.params.oid ? String(this.$route.params.oid) : ''
            } else if (this.entityType === 'space') {
                return this.$route.params.sid ? String(this.$route.params.sid) : ''
            }
            return ''
        },
        isOrganization() {
            return this.entityType === 'organization'
        },
        isSpace() {
            return this.entityType === 'space'
        },
        dialogTitle() {
            return this.isOrganization ? 'Leave Organization' : 'Leave Space'
        },
        defaultButtonText() {
            return this.isOrganization ? 'Leave organization' : 'Leave space'
        },
        warningText() {
            if (this.isSpace) {
                return 'Leaving a space will revoke your access to all instances and contents inside the space. To regain access to the space, please contact a space administrator.'
            } else if (this.isOrganization) {
                return 'Leaving an organization will revoke your access to all spaces and instances inside the organization. To regain access to the organization, please contact an organization manager.'
            }
            return ''
        }
    },
    watch: {
        dialog(val) {
            if (!val) {
                this.agreeBox = false
            }
        }
    },
    methods: {
        performLeave() {
            if (!this.effectiveEntityId) {
                return
            }

            this.leaving = true
            let apiURL

            if (this.isOrganization) {
                apiURL = `/orgs/${this.effectiveEntityId}/leave_async`
            } else if (this.isSpace) {
                apiURL = `/spaces/${this.effectiveEntityId}/leave`
            }

            this.$axios
                .delete(apiURL)
                .then(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: `Successfully revoked your ${this.entityType} role.`,
                        snackBarTimeout: 2000,
                        snackBarIcon: 'check_circle'
                    })
                })
                .catch(error => {
                    const snackBarText = getBackendErrorMessage(error, `Could not revoke your ${this.entityType} role.`)
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText,
                            snackBarTimeout: 2000,
                            snackBarIcon: 'error'
                        })
                    }
                })
                .finally(() => {
                    this.leaving = false
                    this.dialog = false
                    this.agreeBox = false

                    if (this.isOrganization) {
                        this.$store.dispatch('fetchUserOrgs')
                        this.$router.push({ name: 'welcome' })
                    } else if (this.isSpace) {
                        const orgId = this.$route.params.oid
                        this.$store.dispatch('orgStore/fetchOrgSpaces', orgId).finally(() => {
                            this.$router.push({ name: 'home-dashboard', params: { oid: orgId } })
                        })
                    }
                })
        }
    }
}
</script>
